import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, getUser, clearSession } from "../lib/api";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { Wallet, Gift, Copy, Share2, CheckCircle2, Phone, Bus } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const [items, setItems] = useState([]);
  const [wallet, setWallet] = useState({ wallet_balance: 0, completed_trips: 0, trips: 0 });
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const user = getUser();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/bookings/my").then(r => setItems(r.data)).catch(() => {});
    api.get("/customers/me/wallet").then(r => setWallet(r.data)).catch(() => {});
    api.get("/customers/me/referral").then(r => setReferral(r.data)).catch(() => {});
  }, []);

  const copyCode = async () => {
    if (!referral?.referral_code) return;
    try {
      await navigator.clipboard.writeText(referral.share_link || referral.referral_code);
      setCopied(true);
      toast.success("Link copied — share with friends!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — long-press to copy manually"); }
  };

  const shareWA = () => {
    if (!referral?.referral_code) return;
    const msg = encodeURIComponent(
      `Try Shruti Tours for your next trip — use my code *${referral.referral_code}* when you sign up and I get ₹200 wallet credit when you complete your first trip. ${referral.share_link || ""}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-4 pt-10 pb-16">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">My Account</div>
            <h1 className="text-4xl font-display font-black tracking-tight">Welcome back, {user?.name || "Traveler"}</h1>
            <p className="text-slate-500 mt-2">All your Shruti Tours bookings in one place.</p>
          </div>
          <Button variant="outline" onClick={() => { clearSession(); nav("/"); }} data-testid="customer-logout">Logout</Button>
          <Link to="/dashboard/settings" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:border-[#ea580c] hover:text-[#ea580c] transition-colors ml-2" data-testid="customer-settings-link">
            Settings
          </Link>
        </div>

        {/* Wallet + Referral row */}
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-[#0f1c56] flex flex-wrap items-center justify-between gap-4 shadow-lg" data-testid="wallet-card">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8" />
              <div>
                <div className="text-xs uppercase tracking-widest opacity-80">Shruti Wallet</div>
                <div className="text-3xl font-display font-black">₹{Number(wallet.wallet_balance || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="text-right text-xs">
              <div><b>{wallet.completed_trips}</b> completed trips</div>
              <div>2% wallet credit on every trip</div>
            </div>
          </div>

          {referral && (
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1e1b4b] via-[#2d2a6e] to-[#0b1440] text-white shadow-lg" data-testid="referral-card">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-300" />
                <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">Refer & Earn</div>
              </div>
              <div className="mt-2 text-lg font-display font-bold leading-tight">Get ₹200 for every friend who completes their first trip</div>
              <div className="mt-3 p-3 rounded-lg bg-white/10 border border-white/15 flex items-center justify-between gap-2">
                <div className="font-mono font-bold text-lg tracking-wider" data-testid="referral-code">{referral.referral_code}</div>
                <div className="flex gap-1.5">
                  <button onClick={copyCode} className="p-2 rounded-md bg-white/15 hover:bg-white/25 transition-colors" data-testid="referral-copy" title="Copy share link">
                    {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={shareWA} className="p-2 rounded-md bg-emerald-500 hover:bg-emerald-600 transition-colors" data-testid="referral-share-wa" title="Share on WhatsApp">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-white/70">
                <span><b className="text-white">{referral.referred_signups}</b> friends signed up</span>
                <span><b className="text-emerald-300">{referral.successful_referrals}</b> completed a trip</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 grid gap-4">
          {items.length === 0 && <div className="p-6 rounded-xl bg-white border border-slate-200 text-sm text-slate-500">No bookings yet. <Link to="/book" className="text-[#ea580c] font-semibold">Book your first ride →</Link></div>}
          {items.map(b => (
            <Link to={`/book/confirm/${b.booking_id}`} key={b.booking_id} className="card-lift block p-5 rounded-xl bg-white border border-slate-200" data-testid={`my-booking-${b.booking_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-lg text-[#1e1b4b]">{b.from_place} → {b.to_place}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.booking_id} · {b.travel_date} · {b.passengers} pax</div>
                </div>
                <div className="text-right">
                  <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 capitalize">{b.status.replace(/_/g, ' ')}</div>
                  <div className="text-lg font-black text-[#ea580c] mt-2">₹{b.fare.toLocaleString()}</div>
                </div>
              </div>
              {(b.vehicle || b.driver) && (
                <div className="mt-3 pt-3 border-t border-dashed border-slate-200 grid sm:grid-cols-2 gap-2 text-sm" data-testid={`booking-assignment-${b.booking_id}`}>
                  {b.vehicle && (
                    <div className="flex items-start gap-1.5"><Bus className="w-4 h-4 text-[#ea580c] mt-0.5 flex-shrink-0" /><div><div className="text-[11px] text-slate-500">Vehicle</div><span className="font-semibold">{b.vehicle.vehicle_number}</span> · {b.vehicle.vehicle_type}</div></div>
                  )}
                  {b.driver && (
                    <div className="flex items-start gap-1.5"><Phone className="w-4 h-4 text-[#ea580c] mt-0.5 flex-shrink-0" /><div><div className="text-[11px] text-slate-500">Driver</div>{b.driver.name} · <a href={`tel:${b.driver.mobile}`} onClick={e=>e.stopPropagation()} className="text-[#ea580c] font-semibold hover:underline">{b.driver.mobile}</a></div></div>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
